import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, Button, ControlledInput, ControlledSearchableAsyncSelect, Radio, RadioGroup } from '@shared/ui';
import { useCreatePickupPoint, usePickupPoints, useUpdatePickupPoint } from '../../hooks/usePickupPoints';
import type { PickupPoint, PickupPointDeliveryProvider } from '../../types';
import { apiClient } from '@shared/utils/api-client';
import { notifications } from '@mantine/notifications';
import {
  pickupPointModalSchema,
  type PickupPointFormValues,
} from '../../utils/pickupPointModalValidation';
import { formatPickupPhoneForInput } from '../../utils/formatPickupPhone';
import { isMarketplaceDeliveryCity } from '../../utils/marketplaceDeliveryCities';
import PickupPointMetroFields, {
  applyNearestMetroToForm,
  clearMetroFields,
} from '../PickupPointMetroFields';
import { hasMetroCatalog } from '../../data/metroStations';
import './PickupPointModal.css';

interface PickupPointModalProps {
  isOpen: boolean;
  onClose: () => void;
  pickupPoint: PickupPoint | null;
  /** При создании передаётся ответ API с новой точкой; при редактировании — без аргумента. */
  onSuccess?: (created?: PickupPoint) => void;
}

type GeocodingSuggestion = {
  fullName: string;
  city?: string;
  locality?: string;
};

type PendingCreatePayload = {
  city: string;
  address: string;
  metro?: string;
  metroIconColor?: string;
  phoneNumber: string;
  telegram?: string;
  email: string;
  lat: number;
  long: number;
};

const DELIVERY_OPTION_HINTS = {
  MARKETPLACE:
    'Курьеры маркетплейса везут заказ покупателю. Компенсацию части стоимости можно настроить позже.',
  SELLER:
    'Вы сами доставляете заказы. Зоны и цены на карте можно добавить после создания точки.',
} as const;

const defaultFormValues: PickupPointFormValues = {
  city: '',
  address: '',
  metro: '',
  metroIconColor: '',
  phoneNumber: '',
  telegram: '',
  email: '',
  lat: '',
  long: '',
};

function normalizePickupAddress(city: string, address: string): string {
  return `${city} ${address}`.trim().replace(/\s+/g, ' ').toLowerCase();
}

export default function PickupPointModal({ isOpen, onClose, pickupPoint, onSuccess }: PickupPointModalProps) {
  const createPickupPoint = useCreatePickupPoint();
  const updatePickupPoint = useUpdatePickupPoint();
  const { data: existingPickupPoints = [] } = usePickupPoints();
  const isEdit = !!pickupPoint;
  const [createStep, setCreateStep] = useState<'details' | 'delivery'>('details');
  const [pendingCreate, setPendingCreate] = useState<PendingCreatePayload | null>(null);
  const [deliveryProvider, setDeliveryProvider] = useState<PickupPointDeliveryProvider>('MARKETPLACE');
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
  } = useForm<PickupPointFormValues>({
    resolver: yupResolver(pickupPointModalSchema),
    defaultValues: defaultFormValues,
  });
  const address = watch('address');
  const city = watch('city');
  const marketplaceAvailable = isMarketplaceDeliveryCity(pendingCreate?.city ?? city);

  useEffect(() => {
    if (!isOpen) return;
    setCreateStep('details');
    setPendingCreate(null);
    setDeliveryProvider('MARKETPLACE');
    if (pickupPoint) {
      reset({
        city: pickupPoint.city,
        address: pickupPoint.address,
        metro: pickupPoint.metro ?? '',
        metroIconColor: pickupPoint.metroIconColor ?? '',
        phoneNumber: pickupPoint.phoneNumber ? formatPickupPhoneForInput(pickupPoint.phoneNumber) : '',
        telegram: pickupPoint.telegram ?? '',
        email: pickupPoint.email ?? '',
        lat: pickupPoint.lat.toString(),
        long: pickupPoint.long.toString(),
      });
    } else {
      reset(defaultFormValues);
    }
  }, [pickupPoint, isOpen, reset]);

  useEffect(() => {
    if (createStep !== 'delivery' || !pendingCreate) return;
    if (!isMarketplaceDeliveryCity(pendingCreate.city)) {
      setDeliveryProvider('SELLER');
    }
  }, [createStep, pendingCreate]);

  const loadAddressOptions = useCallback(async (query: string) => {
    const result = await apiClient.get<{ suggestions: GeocodingSuggestion[] }>(
      `/geocoding/cities?query=${encodeURIComponent(query)}`,
    );
    return (result.suggestions ?? []).map((s) => ({
      value: s.fullName,
      label: s.fullName,
      meta: s,
    }));
  }, []);

  const resolveCoordinatesByAddress = async (
    sourceAddress?: string,
    options?: { suggestMetro?: boolean; overwriteMetro?: boolean },
  ) => {
    const query = (sourceAddress ?? address).trim();
    if (!query) return;
    try {
      const data = await apiClient.get<{
        lat: number;
        lon: number;
        city?: string;
        address?: string;
        streetAddress?: string;
        street?: string;
        house?: string;
      }>(`/geocoding/coordinates?address=${encodeURIComponent(query)}`);
      setValue('lat', String(data.lat));
      setValue('long', String(data.lon));
      const resolvedCity = (data.city ?? '').trim();
      if (resolvedCity) setValue('city', resolvedCity);
      // В инпуте оставляем выбранную полную подсказку — streetAddress в payload, не в поле.
      const streetAddress = (data.streetAddress ?? '').trim();
      if (!streetAddress) {
        notifications.show({
          title: 'Адрес неполный',
          message: 'Выберите адрес с улицей и домом из подсказки',
          color: 'red',
        });
      }
      if (!hasMetroCatalog(resolvedCity || city)) {
        clearMetroFields(setValue);
      } else if (options?.suggestMetro !== false) {
        await applyNearestMetroToForm({
          lat: data.lat,
          lon: data.lon,
          city: resolvedCity || city,
          setValue,
          onlyIfEmpty: !options?.overwriteMetro,
          currentMetro: watch('metro'),
        });
      }
      return data;
    } catch {
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось определить координаты по адресу',
        color: 'red',
      });
    }
  };

  const buildCreatePayload = async (
    values: PickupPointFormValues,
  ): Promise<PendingCreatePayload | null> => {
    const geocoded = await resolveCoordinatesByAddress(values.address, {
      suggestMetro: true,
      overwriteMetro: false,
    });
    if (!geocoded) return null;
    const latNum = Number(geocoded.lat);
    const longNum = Number(geocoded.lon);
    const cityValue = (geocoded.city ?? values.city).trim();
    const addressValue = (geocoded.streetAddress ?? '').trim();
    const normalizedNew = normalizePickupAddress(cityValue, addressValue);
    const hasDuplicateAddress = existingPickupPoints.some(
      (point) =>
        point.id !== pickupPoint?.id &&
        normalizePickupAddress(point.city, point.address) === normalizedNew,
    );
    if (hasDuplicateAddress) {
      setError('address', {
        type: 'duplicate',
        message: 'Точка самовывоза по этому адресу уже добавлена',
      });
      return null;
    }
    if (!Number.isFinite(latNum) || !Number.isFinite(longNum) || !cityValue || !addressValue) {
      notifications.show({
        title: 'Ошибка адреса',
        message: 'Не удалось получить корректные координаты/город по адресу',
        color: 'red',
      });
      return null;
    }

    const emailTrimmed = values.email.trim();
    const phoneDigits = values.phoneNumber.replace(/\D/g, '');
    const metro = watch('metro');
    const metroIconColor = watch('metroIconColor');
    return {
      city: cityValue,
      address: addressValue,
      metro: metro || undefined,
      metroIconColor: metroIconColor || undefined,
      phoneNumber: phoneDigits,
      telegram: values.telegram || undefined,
      email: emailTrimmed,
      lat: latNum,
      long: longNum,
    };
  };

  const onSubmitDetails = async (values: PickupPointFormValues) => {
    try {
      const payload = await buildCreatePayload(values);
      if (!payload) return;

      if (isEdit && pickupPoint) {
        await updatePickupPoint.mutateAsync({
          id: pickupPoint.id,
          data: payload,
        });
        reset(defaultFormValues);
        onSuccess?.();
        onClose();
        return;
      }

      setPendingCreate(payload);
      setDeliveryProvider(
        isMarketplaceDeliveryCity(payload.city) ? 'MARKETPLACE' : 'SELLER',
      );
      setCreateStep('delivery');
    } catch {
      // Ошибки уже обрабатываются в хуках
    }
  };

  const onConfirmDelivery = async () => {
    if (!pendingCreate) return;
    try {
      const created = await createPickupPoint.mutateAsync({
        ...pendingCreate,
        deliveryProvider,
      });
      reset(defaultFormValues);
      setPendingCreate(null);
      setCreateStep('details');
      onSuccess?.(created);
      onClose();
    } catch {
      // Ошибки уже обрабатываются в хуках
    }
  };

  const handleClose = () => {
    reset(defaultFormValues);
    setPendingCreate(null);
    setCreateStep('details');
    setDeliveryProvider('MARKETPLACE');
    onClose();
  };

  const modalTitle = isEdit
    ? 'Редактировать точку самовывоза'
    : createStep === 'delivery'
      ? 'Способ доставки'
      : 'Добавить точку самовывоза';

  return (
    <Modal
      isOpen={isOpen}
      size={500}
      onClose={handleClose}
      title={modalTitle}
    >
      {createStep === 'delivery' && !isEdit ? (
        <div className="pickup-point-modal-form">
          <p className="pickup-point-modal__delivery-intro">
            Выберите, кто будет доставлять заказы с этой точки. Способ нельзя будет изменить позже —
            зоны доставки и компенсацию можно настроить в карточке точки.
          </p>
          <RadioGroup
            label=""
            value={deliveryProvider}
            onChange={(value) => setDeliveryProvider(value as PickupPointDeliveryProvider)}
          >
            <div className="pickup-point-modal__delivery-radios">
              <Radio
                value="MARKETPLACE"
                label="Партнерами маркетплейса"
                description={
                  marketplaceAvailable
                    ? DELIVERY_OPTION_HINTS.MARKETPLACE
                    : 'Для этого города доставка партнёрами недоступна'
                }
                disabled={!marketplaceAvailable}
              />
              <Radio
                value="SELLER"
                label="Силами продавца"
                description={DELIVERY_OPTION_HINTS.SELLER}
              />
            </div>
          </RadioGroup>
          <div className="pickup-point-modal__actions">
            <Button
              type="button"
              onClick={() => setCreateStep('details')}
              variant="secondary"
            >
              Назад
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={createPickupPoint.isPending}
              onClick={() => void onConfirmDelivery()}
            >
              Создать точку
            </Button>
          </div>
        </div>
      ) : (
        <form noValidate onSubmit={handleSubmit(onSubmitDetails)} className="pickup-point-modal-form">
          <ControlledSearchableAsyncSelect<PickupPointFormValues, GeocodingSuggestion>
            label="Адрес"
            name="address"
            control={control}
            placeholder="Начните вводить адрес и выберите из списка"
            loadOptions={loadAddressOptions}
            nothingFoundMessage="Ничего не найдено"
            onOptionSelect={(option) => {
              const s = option.meta;
              if (s && (s.city || s.locality)) setValue('city', (s.city ?? s.locality ?? '').trim());
              void resolveCoordinatesByAddress(option.value, { overwriteMetro: true });
            }}
          />
          <PickupPointMetroFields
            city={city}
            visible={Boolean(city.trim())}
            setValue={setValue}
            watch={watch}
          />
          <ControlledInput
            label="Номер телефона"
            name="phoneNumber"
            control={control}
            formatOnChange={formatPickupPhoneForInput}
            placeholder="+7 (XXX) XXX-XX-XX"
            description="При экстренных ситуациях, важных вопросах и спорных моментах будем звонить на этот номер"
          />
          <ControlledInput
            label="Email"
            type="text"
            name="email"
            control={control}
            placeholder="order@example.com"
            description="На этот email будут приходить уведомления о новых заказах"
            autoComplete="email"
            inputMode="email"
          />
          <div className="pickup-point-modal__actions">
            <Button type="button" onClick={handleClose} variant="secondary">
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={updatePickupPoint.isPending}
            >
              {isEdit ? 'Сохранить' : 'Далее'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
